import { call, put, select, takeLatest } from 'typed-redux-saga'
import {
	errorHandler,
	notifyMessageCreator,
	NOTIFICATION_TYPE,
	showNotificationAction,
	loadersSlice,
	gridSlice,
} from 'Modules'
import { Deserialize } from 'cerialize'
import { ResponseCatalog } from 'Models'
import {
	externalFormsApi,
	getExternalFormsSelector,
	ProductModel,
	ExternalFormModel,
	ExternalFormNS,
	externalFormSlice,
	getParamsFromUrl,
} from 'Pages/orders'
import { BlockError } from 'Pages/orders_design'
import { takeEveryUnique } from 'Helpers'

const keyCodes: string[] = [BlockError.blocked_by_me, BlockError.blocked_by_other]

function* getAll(action: ExternalFormNS.GetAll) {
	try {
		const { params, clear } = action.payload
		yield* put(gridSlice.actions.setLoading(true))
		if (clear) {
			yield* put(externalFormSlice.actions.clearAll())
		}
		const res = yield* call(externalFormsApi.getAll, params)
		const externalForms = new ResponseCatalog<ExternalFormModel>(res)
		externalForms.entries = externalForms.entries.map(
			(item): ExternalFormModel => Deserialize(item, ExternalFormModel)
		)
		if (!clear) {
			const oldItems = yield* select(getExternalFormsSelector.getAll)
			externalForms.entries = [...oldItems, ...externalForms.entries]
		}
		yield* put(externalFormSlice.actions.setAll(externalForms))
		yield* put(externalFormSlice.actions.setRequestParams(params))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* getOne(action: ExternalFormNS.GetOne) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(externalFormsApi.getOne, action.payload)
		const externalForm = Deserialize(res, ExternalFormModel)
		yield* put(externalFormSlice.actions.setOne(externalForm))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* createOne(action: ExternalFormNS.CreateOne) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		yield* call(externalFormsApi.createOne, action.payload)
		const params = yield* select(getExternalFormsSelector.getParams)
		yield* put(externalFormSlice.actions.getAll({ params, clear: true }))
		yield* call(notifyMessageCreator, 'orders.external_form_create.success')
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* updateOne(action: ExternalFormNS.UpdateOne) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id, externalForm } = action.payload
		const res = yield* call(externalFormsApi.updateOne, externalForm, id)
		yield* put(externalFormSlice.actions.setOneUpdate(res))
		yield* call(notifyMessageCreator, 'orders.external_form_update.success')
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* deleteOne(action: ExternalFormNS.DeleteOne) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(externalFormsApi.deleteOne, action.payload.token)
		if (res.result === 'ok') {
			yield* put(externalFormSlice.actions.setOneUpdate({ ...action.payload, is_deleted: true }))
			yield* call(notifyMessageCreator, 'orders.external_form_delete.success')
		}
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* restoreOne(action: ExternalFormNS.RestoreOne) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(externalFormsApi.restoreOne, action.payload.token)
		if (res.result === 'ok') {
			yield* put(externalFormSlice.actions.setOneUpdate({ ...action.payload, is_deleted: false }))
			yield* call(notifyMessageCreator, 'orders.external_form_restore.success')
		}
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* getOrderBuild(action: ExternalFormNS.GetOrderBuild) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(externalFormsApi.getOrderBuild, action.payload, getParamsFromUrl())
		yield* put(externalFormSlice.actions.setOrderBuild(res))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* createOrder(action: ExternalFormNS.CreateOrderBuild) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { token, data, showMessage = true, callback } = action.payload

		const res = yield* call(externalFormsApi.createOrder, token, {
			...data,
			...getParamsFromUrl(),
		})
		if (showMessage) {
			yield* call(notifyMessageCreator, 'common.create_request.success')
		}
		callback?.(res)
	} catch (error) {
		const { errorCallback } = action.payload
		yield* call(errorHandler, error, null, {
			keyCodes,
			substitutedValue: 'external_form',
		})
		errorCallback?.(error.data?.errors)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* sendPostback(action: ExternalFormNS.SendPostback) {
	try {
		const { clickId, trackId, partnerInn } = action.payload
		yield* call(externalFormsApi.sendPostback, clickId, trackId, partnerInn)
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* getProducts(action: ExternalFormNS.GetProducts) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(externalFormsApi.getProducts, action.payload)
		const products = res.map((p): ProductModel => Deserialize(p, ProductModel))
		yield* put(externalFormSlice.actions.setProducts(products))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* markAdvExternalForm(action: ExternalFormNS.MarkExternalForm) {
	try {
		const { data, token, callback } = action.payload
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(externalFormsApi.markAdvExternalForm, data, token)
		yield* put(externalFormSlice.actions.setOneUpdate(res))
		yield* call(notifyMessageCreator, 'orders.external_form_marking.success')
		callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* adMarkingStateSaga(action: ExternalFormNS.AdMarkingState) {
	try {
		const { id } = action.payload
		const extFormsList = yield* select(getExternalFormsSelector.getAll)
		const index = extFormsList?.find((form): boolean => form.id === id)
		if (index) {
			const params = yield* select(getExternalFormsSelector.getParams)
			yield* put(externalFormSlice.actions.getAll(params))
			yield* put(
				showNotificationAction({
					type: NOTIFICATION_TYPE.SUCCESS,
					message: `Регистрация маркера для формы ${id} прошла успешно`,
				})
			)
		}
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* sendReport(action: ExternalFormNS.SendReport) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { params } = action.payload
		yield* call(externalFormsApi.sendReport, params)
		yield* call(notifyMessageCreator, 'common.report_send.success')
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* getRegionIp(action: ExternalFormNS.GetRegionIp) {
	try {
		const regions = yield* call(externalFormsApi.getRegions)
		const ip = yield* call(externalFormsApi.getIp)
		const address = yield* call(externalFormsApi.getDadataAddressByIp, ip)
		const nameRegion = address.location?.data.region || ''
		const nameRegionType = address.location?.data.region_type_full || ''
		const name = `${nameRegion} ${nameRegionType}`.toLocaleLowerCase().trim()
		const findRegion = (regions?.entries ?? []).find(
			(region) => region.name.toLocaleLowerCase() === name
		)?.id
		yield* put(externalFormSlice.actions.setRegionIp(findRegion))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* uploadInstructionFile(action: ExternalFormNS.UploadInstructionFile) {
	try {
		yield* put(gridSlice.actions.setLoading(true))
		const { file, callback } = action.payload
		yield* call(externalFormsApi.createInstruction, file)
		yield* put(gridSlice.actions.setLoading(false))
		callback?.()
		yield* call(notifyMessageCreator, 'external_form_upload_success')
	} catch (error) {
		yield* put(gridSlice.actions.setLoading(false))
		yield* call(errorHandler, error)
	}
}

export function* getInstructionFile(action: ExternalFormNS.GetInstructionFile) {
	try {
		yield* put(gridSlice.actions.setLoading(true))
		const file = yield* call(externalFormsApi.getInstructionFile)
		yield* put(externalFormSlice.actions.setInstructionFile(file))
		yield* put(gridSlice.actions.setLoading(false))
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

export function* getWithInstruction(action: ExternalFormNS.GetWithInstructionFlag) {
	try {
		const res = yield* call(externalFormsApi.getWithInstructionFlag, { for_page: true })
		yield* put(externalFormSlice.actions.setWithInstructionFlag(res?.with_instruction))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

export const externalFormSaga = function* saga() {
	yield* takeLatest(externalFormSlice.actions.getAll.type, getAll)
	yield* takeLatest(externalFormSlice.actions.getOne.type, getOne)
	yield* takeLatest(externalFormSlice.actions.createOne.type, createOne)
	yield* takeLatest(externalFormSlice.actions.updateOne.type, updateOne)
	yield* takeLatest(externalFormSlice.actions.deleteOne.type, deleteOne)
	yield* takeLatest(externalFormSlice.actions.restoreOne.type, restoreOne)
	yield* takeLatest(externalFormSlice.actions.getOrderBuild.type, getOrderBuild)
	yield* takeLatest(externalFormSlice.actions.createOrderBuild.type, createOrder)
	yield* takeLatest(externalFormSlice.actions.sendPostback.type, sendPostback)
	yield* takeLatest(externalFormSlice.actions.getProducts.type, getProducts)
	yield* takeEveryUnique(externalFormSlice.actions.markExternalForm.type, markAdvExternalForm)
	yield* takeLatest(externalFormSlice.actions.adMarkingState.type, adMarkingStateSaga)
	yield* takeLatest(externalFormSlice.actions.sendReport.type, sendReport)
	yield* takeLatest(externalFormSlice.actions.getRegionIp.type, getRegionIp)
	yield* takeLatest(externalFormSlice.actions.uploadInstructionFile.type, uploadInstructionFile)
	yield* takeLatest(externalFormSlice.actions.getInstructionFile.type, getInstructionFile)
	yield* takeLatest(externalFormSlice.actions.getWithInstructionFlag.type, getWithInstruction)
}
